/**
 * src/i18n/dicts.js
 *
 * Per-locale dictionary loading. Owns WHEN a dictionary is in memory; t.js
 * owns what you do with one.
 *
 * WHY THIS EXISTS. t.js used to `import { en } from './en.js'` and
 * `import { ar } from './ar.js'` at module scope. Both are static imports, so
 * Vite folded both dictionaries into the ENTRY chunk — the one file every
 * visitor must download and parse before anything renders. Measured on the
 * 2026-08-17 build: entry = 1354 KB raw / 313 KB gzipped, and the two
 * dictionaries alone are 1363 KB of source (en 595 KB + ar 768 KB). The entry
 * chunk was, essentially, both dictionaries.
 *
 * Every English user was downloading and parsing the complete Arabic
 * dictionary they would never see, and vice versa. Not a rendering cost — a
 * download-and-parse cost paid on first paint, on the slowest connection, on
 * the oldest phone.
 *
 * WHY DROPPING THE OTHER DICTIONARY IS SAFE. t.js's lookup chain was
 * `active dict -> en dict -> raw key`, so the en dictionary was a
 * cross-language fallback for a key missing from ar. That fallback is
 * provably dead:
 *
 *   - key sets are IDENTICAL — 9412 keys each, zero missing in either
 *     direction (measured 2026-08-19);
 *   - parity is not a coincidence, it is enforced: `scripts/i18n-smoke.mjs`
 *     diffs Object.keys(en) against Object.keys(ar), which is exactly why the
 *     dictionaries are flat-keyed in the first place (see the header of
 *     en.js);
 *   - every value in both dictionaries is a NON-EMPTY string (measured:
 *     0 falsy, 0 non-string). This one matters more than it looks. The old
 *     chain was `(has(k) && active[k]) || (has(k) && en[k]) || k` — a `&&`
 *     that yields the VALUE, so an empty-string translation would have fallen
 *     through to English. With no empty values, that path never ran.
 *
 * So for a given key the fallback either returned the active dictionary's own
 * value or was never reached. Loading one dictionary changes no rendered
 * string.
 *
 * The fallback is still WIRED in t.js — peekDict('en') is consulted when it
 * happens to be resident — so if parity ever breaks, behaviour degrades to
 * "raw key" (loud, obvious) rather than silently blank. It is a belt, not the
 * mechanism.
 *
 * KEEPING t() SYNCHRONOUS. t() is called from 137 modules inside render
 * paths; making it async would rewrite the whole app. So the async boundary
 * lives HERE and is crossed exactly twice — once at boot (main.js awaits the
 * persisted language) and once per language toggle (store.js's setLang awaits
 * the incoming language before it notifies anyone). By the time any t() runs,
 * its dictionary is resident.
 */

/**
 * Dynamic imports, one per locale. These MUST stay dynamic — a static import
 * anywhere in the graph pulls the dictionary straight back into the entry
 * chunk and silently undoes this entire file. `scripts/i18n-smoke.mjs` and
 * the gate-checks import en.js/ar.js directly, which is fine: they are Node
 * scripts and never enter the browser bundle.
 */
const LOADERS = {
  en: () => import('./en.js').then((m) => m.en),
  ar: () => import('./ar.js').then((m) => m.ar),
};

/** lang -> resolved dictionary object. */
const _loaded = Object.create(null);
/** lang -> in-flight promise, so concurrent callers share one network fetch. */
const _inflight = Object.create(null);

/**
 * Loads a locale's dictionary, memoized. Safe to call repeatedly and
 * concurrently: the second call for a language already in flight returns the
 * same promise rather than starting a second fetch.
 *
 * Rejects only if the chunk itself fails to load (offline, bad deploy). The
 * caller decides what that means — main.js mounts anyway rather than showing
 * a blank page, because a UI rendering raw keys is recoverable and a blank
 * page is not.
 *
 * @param {string} lang — 'en' | 'ar'
 * @returns {Promise<Object>} the flat dictionary
 */
export function loadDict(lang) {
  const key = LOADERS[lang] ? lang : 'en';
  if (_loaded[key]) return Promise.resolve(_loaded[key]);
  if (_inflight[key]) return _inflight[key];

  const p = LOADERS[key]()
    .then((dict) => {
      _loaded[key] = dict;
      delete _inflight[key];
      return dict;
    })
    .catch((err) => {
      // Clear the in-flight slot so a later attempt can retry rather than
      // permanently resolving to a rejected promise.
      delete _inflight[key];
      throw err;
    });

  _inflight[key] = p;
  return p;
}

/**
 * Returns a dictionary ONLY if it is already resident. Never triggers a load
 * and never blocks — this is what the synchronous t()/tn() lookup path calls.
 *
 * @param {string} lang
 * @returns {Object|null}
 */
export function peekDict(lang) {
  return _loaded[lang] || null;
}

/** True when `lang`'s dictionary is resident and t() can render it. */
export function isDictLoaded(lang) {
  return Boolean(_loaded[lang]);
}

/**
 * Warms a dictionary without blocking anything and without surfacing a
 * failure. Used to pull the OTHER language in after first paint, so hitting
 * the language toggle is instant rather than costing a fetch. Deliberately
 * swallows its error: this is opportunistic, and a failed warm must never
 * reach a user or a console the way a real failure should.
 *
 * @param {string} lang
 */
export function warmDict(lang) {
  if (!LOADERS[lang] || _loaded[lang] || _inflight[lang]) return;
  loadDict(lang).catch(() => {});
}

/** Locales this module can load. */
export const LOADABLE_LANGS = Object.keys(LOADERS);
